import { apiFetch } from '../api/client.js';

export type PushSupport =
  | { ok: true }
  | { ok: false; reason: 'no_service_worker' | 'no_push_manager' | 'no_notification_api' | 'insecure_context' };

export function checkSupport(): PushSupport {
  if (typeof window === 'undefined') return { ok: false, reason: 'no_service_worker' };
  if (!window.isSecureContext) return { ok: false, reason: 'insecure_context' };
  if (!('serviceWorker' in navigator)) return { ok: false, reason: 'no_service_worker' };
  if (!('PushManager' in window)) return { ok: false, reason: 'no_push_manager' };
  if (!('Notification' in window)) return { ok: false, reason: 'no_notification_api' };
  return { ok: true };
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function bufToBase64Url(buf: ArrayBuffer | null): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function getSubscription(): Promise<PushSubscription | null> {
  if (!checkSupport().ok) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`timeout_${label}_${ms}ms`)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

export async function subscribePush(): Promise<{ ok: true } | { ok: false; reason: string }> {
  const sup = checkSupport();
  if (!sup.ok) return { ok: false, reason: sup.reason };

  let perm: NotificationPermission;
  try {
    perm = await withTimeout(Notification.requestPermission(), 30_000, 'permission_prompt');
  } catch (e) {
    return { ok: false, reason: (e as Error).message };
  }
  if (perm !== 'granted') return { ok: false, reason: 'permission_denied' };

  let key: string;
  try {
    const r = await apiFetch<{ publicKey: string }>('/api/push/vapid-key');
    key = r.publicKey;
  } catch (e) {
    return { ok: false, reason: 'vapid_key_fetch_failed: ' + (e as Error).message };
  }

  let reg: ServiceWorkerRegistration;
  try {
    reg = await withTimeout(navigator.serviceWorker.ready, 10_000, 'sw_ready');
  } catch (e) {
    return { ok: false, reason: 'sw_not_ready: ' + (e as Error).message };
  }

  try {
    const existing = await reg.pushManager.getSubscription();
    if (existing) await existing.unsubscribe();
  } catch (e) {
    return { ok: false, reason: 'unsubscribe_existing_failed: ' + (e as Error).message };
  }

  let sub: PushSubscription;
  try {
    sub = await withTimeout(reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key).buffer as ArrayBuffer,
    }), 20_000, 'subscribe');
  } catch (e) {
    return { ok: false, reason: 'pushmanager_subscribe_failed: ' + (e as Error).message };
  }

  try {
    await apiFetch('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: sub.endpoint,
        p256dh: bufToBase64Url(sub.getKey('p256dh')),
        auth: bufToBase64Url(sub.getKey('auth')),
      }),
    });
  } catch (e) {
    return { ok: false, reason: 'server_subscribe_failed: ' + (e as Error).message };
  }
  return { ok: true };
}

export async function unsubscribePush(): Promise<void> {
  const sub = await getSubscription();
  if (!sub) return;
  try {
    await apiFetch('/api/push/unsubscribe', {
      method: 'POST', body: JSON.stringify({ endpoint: sub.endpoint }),
    });
  } catch { /* ignore */ }
  await sub.unsubscribe();
}

export async function sendTestPush(): Promise<{ sent: number }> {
  return apiFetch<{ ok: true; sent: number }>('/api/push/test', { method: 'POST' })
    .then((r) => ({ sent: r.sent }));
}
